import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useToast,
  SimpleGrid,
  useBreakpointValue,
  Wrap,
  Select,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { useTranslation } from "react-i18next";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import CreateSetting from "./createSetting";

const SettingsList = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "lg" });
  const pageId = 20; // Page ID for settings management
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editableSetting, setEditableSetting] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
position: "top-right",

        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === pageId);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
position: "top-right",

      });
    }
  }, []);
  
  // Fetch all settings from GET /settings
  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/settings?page_id=${pageId}`,
        "GET"
      );
      if (response.status === "success") {
        // Assuming the response.data is an array of settings
        setSettings(response.data);
      } else {
        throw new Error("Failed to fetch settings.");
      }
    } catch (error) {
      toast({
        title: t("Error fetching settings."),
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Handle "Edit" action: open modal pre-filled with selected setting data.
  const handleEdit = (setting) => {
    setEditableSetting(setting);
    setIsEditModalOpen(true);
  };

  // Handle "Delete" action: call DELETE /settings/delete/{id} and update state.
  const handleDelete = async (settingId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/settings/delete/${settingId}`,
        "DELETE"
      );
      if (response.status === "success") {
        toast({
          title: t("Setting deleted successfully."),
          status: "success",
          duration: 5000,
          isClosable: true,
position: "top-right",

        });
        setSettings(settings.filter((s) => s.id !== settingId));
      } else {
        throw new Error(response.message || "Failed to delete setting.");
      }
    } catch (error) {
      toast({
        title: t("Error deleting setting."),
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    }
  };

  // Handle saving an edited setting (calls PATCH /settings/update).
  const handleSave = async () => {
    try {
      // Create updated settings array with the modified setting
      const updatedSettings = settings.map(setting => 
        setting.id === editableSetting.id ? editableSetting : setting
      );
      
      const payload = {
        fees: updatedSettings.map((setting) => ({
          key: setting.key,
          value: setting.value === "" ? null : setting.value,
          type: setting.type || null, // Default to "fixed" if type is not set
        })),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/settings/update?page_id=${pageId}`,
        "PATCH",
        JSON.stringify(payload),
        { "Content-Type": "application/json" }
      );
      if (response.status === "success") {
        toast({
          title: t("Setting updated successfully."),
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",

        });
        setSettings(
          settings.map((s) =>
            s.id === editableSetting.id ? editableSetting : s
          )
        );
        setIsEditModalOpen(false);
        setEditableSetting(null);
      } else {
        throw new Error(response.message || "Failed to update setting.");
      }
    } catch (error) {
      toast({
        title: t("Error updating setting."),
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  return (
    <Card p="20px">
      {/* Header with title and Create Setting button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="4">

        {/* <Button colorScheme="cyan" color={"white"} onClick={() => setIsCreateModalOpen(true)}>
          {t("Create Setting")}
        </Button> */}
      </Box>

      {isLoading ? (
        <Text>{t("Loading settings...")}</Text>
      ) : (
        <Table variant="simple">
          <Thead>
            <Tr bg="gray.200">
              <Th>{t("Index")}</Th>
              <Th>{t("Key")}</Th>
              <Th>{t("Type")}</Th>
              <Th>{t("Value")}</Th>
              <Th>{t("Actions")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {settings.map((setting, index) => (
              <Tr key={setting.id}>
                <Td>{index + 1}</Td>
                <Td>{setting.key}</Td>
                <Td>{setting.type ? setting.type : "Fixed"}</Td>
                <Td>{setting.value}</Td>
                <Td>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    mr="2"
                    onClick={() => handleEdit(setting)}
                  >
                    {t("Edit")}
                  </Button>
                  {/* <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => handleDelete(setting.id)}
                  >
                    {t("Delete")}
                  </Button> */}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {/* Edit Setting Modal */}
      {isEditModalOpen && editableSetting && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          size={modalSize}
        >
          <ModalOverlay />
          <ModalContent>  
            <ModalCloseButton />
            <ModalBody>
              <SimpleGrid columns={1} spacing={4}>
                <Box>
                  <Text mb="2" fontSize="sm">
                    {t("Key")}
                  </Text>
                  <Input
                    type="text"
                    value={editableSetting.key}
                    onChange={(e) =>
                      setEditableSetting({
                        ...editableSetting,
                        key: e.target.value,
                      })
                    }
                    isReadOnly
                  />
                </Box>

                {editableSetting.key === "item_commission" &&
                  <Box>
                    <Text mb="2" fontSize="sm">
                      {t("Type")}
                    </Text>
                    <Select
                      variant="filled"
                      placeholder={t("Select Type")}
                      value={editableSetting.type}
                      onChange={(e) =>
                        setEditableSetting({
                          ...editableSetting,
                          type: e.target.value,
                        })
                      }
                    >
                      <option value="fixed">{t("Fixed")}</option>
                      <option value="percentage">{t("Percentage")}</option>
                    </Select>
                  </Box>
                }
                <Box>
                  <Text mb="2" fontSize="sm">
                    {t("Value")}
                  </Text>
                  <Input
                    type="number"
                    placeholder={t("Enter value")}
                    min={0}
                    step="1.00"
                    value={editableSetting.value}
                    onChange={(e) =>
                      setEditableSetting({
                        ...editableSetting,
                        value: e.target.value,
                      })
                    }
                  />
                </Box>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleSave}>
                {t("Save")}
              </Button>
              <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>
                {t("Cancel")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Create Setting Modal */}
      {isCreateModalOpen && (
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          size={modalSize}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("Create Setting")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <CreateSetting />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Card>
  );
};

export default SettingsList;
